import { Badge, Box, Flex, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FiAlertOctagon } from "react-icons/fi";
import { api } from "../../../services/api";
import { EditBatch } from "../EditBatch";

const RED = "#e74c3c";

function fmtDate(val) {
  const d = new Date(val);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

// A batch with negative remaining stock (more paid orders than registered
// stock — mirrors EventCalculator::ProductFinder's exact formula, unclamped)
// simply disappears from the calculator's recommendations with no error
// anywhere — this is the only place that surfaces it, so an admin can bump
// the batch's stock back up (reusing the existing EditBatch modal) and have
// it reappear.
export function NegativeStockAlert() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  async function fetchNegativeStock() {
    try {
      const response = await api.get("/api/v1/admin/panels/get_negative_stock_batches");
      setBatches(response.data || []);
    } catch (error) {
      console.error("Error fetching negative stock batches:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchNegativeStock(); }, []);

  function handleBatchUpdated(updatedBatch) {
    const remaining = Number(updatedBatch.stock) - batches.find(b => b.batch_id === updatedBatch.id)?.paid_quantity;
    if (remaining >= 0) {
      setBatches(prev => prev.filter(b => b.batch_id !== updatedBatch.id));
    } else {
      setBatches(prev => prev.map(b => b.batch_id === updatedBatch.id
        ? { ...b, stock: Number(updatedBatch.stock), remaining_stock: remaining }
        : b
      ));
    }
  }

  if (loading || batches.length === 0) return null;

  return (
    <Box
      bg="white" borderRadius="16px" border={`1px solid ${RED}4d`}
      boxShadow="0 2px 12px rgba(0,74,51,.05)" p="16px 18px" mb="20px"
    >
      <Flex align="center" gap="8px" mb="4px">
        <FiAlertOctagon size={15} color={RED} />
        <Text fontSize="12px" fontWeight="800" color={RED}>
          {batches.length} lote{batches.length !== 1 ? "s" : ""} com estoque negativo — sumido{batches.length !== 1 ? "s" : ""} da calculadora
        </Text>
      </Flex>
      <Text fontSize="11px" color="gray.400" mb="10px">
        Mais pedidos pagos do que estoque cadastrado no lote. Ajuste o estoque pra voltar a aparecer na calculadora.
      </Text>
      <Box display="flex" flexDirection="column" gap="2px">
        {batches.map(b => (
          <Flex key={b.batch_id} justify="space-between" align="center" py="6px" borderBottom="1px solid rgba(0,74,51,.05)" gap="8px">
            <Box minW="0">
              <Text fontSize="12px" fontWeight="700" color="gray.700" noOfLines={1}>
                {b.product_name || `Produto #${b.product_id}`}
              </Text>
              <Text fontSize="10px" color="gray.400">
                Lote #{b.batch_id} · estoque {b.stock} · vendido {b.paid_quantity} · expira {fmtDate(b.expiration_date)}
              </Text>
            </Box>
            <Flex align="center" gap="8px" flexShrink={0}>
              <Badge colorScheme="red" fontSize="9px" borderRadius="4px">
                {b.remaining_stock} un.
              </Badge>
              <EditBatch
                productId={b.product_id}
                productBatch={{
                  id: b.batch_id,
                  stock: b.stock,
                  discount_per_centage: b.discount_per_centage,
                  start_date: b.start_date,
                  expiration_date: b.expiration_date,
                  unity_of_measurement: b.unity_of_measurement,
                }}
                onUpdateBatch={handleBatchUpdated}
              />
            </Flex>
          </Flex>
        ))}
      </Box>
    </Box>
  );
}
